import { ROUTES } from '../routes'

const authenticationApiBaseUrl = import.meta.env.VITE_AUTHENTICATION_API_URL ?? 'http://localhost:8080'

export const AUTH_REDIRECT_PARAM = 'isRedirectFromAuth'

// ponytail: hardcoded to the same "bmw" identity provider cdh-data-portal-internal registers with this
// Authentication API (config/base.json) until ops-hub exposes a real identity-provider picker.
export const DEFAULT_IDENTITY_PROVIDER_ID = 'bmw'

export interface CurrentUser {
  id: string
  fullName: string
  initials: string
  email?: string
  role?: string
}

interface CurrentUserResponse {
  user: Partial<CurrentUser> & Pick<CurrentUser, 'id'>
}

/** Thrown by fetchCurrentUser when there is no authenticated session (HTTP 401). */
export class UnauthenticatedError extends Error {
  constructor() {
    super('No authenticated session')
    this.name = 'UnauthenticatedError'
  }
}

function profileFromIdentity(id: string): Pick<CurrentUser, 'fullName' | 'initials'> {
  const nameParts = id.split('@')[0].split('.')
  const hasExpectedFormat = nameParts.length >= 2
  const toNamePart = (part: string) => part.charAt(0).toUpperCase() + part.slice(1)
  const firstName = hasExpectedFormat
    ? nameParts[0].split('-').map(toNamePart).join('-')
    : null
  const middleName = nameParts.length > 2 ? nameParts.slice(1, -1).map((part) => part.toUpperCase()).join(' ') : ''
  const lastName = hasExpectedFormat
    ? nameParts[nameParts.length - 1].split('-').map(toNamePart).join('-')
    : null

  return {
    fullName: firstName && lastName ? `${firstName} ${middleName ? `${middleName} ` : ''}${lastName}` : id,
    initials: firstName && lastName ? `${firstName[0]}${lastName[0]}` : id.charAt(0).toUpperCase(),
  }
}

// A safe return path must be an app-internal path (starts with a single "/"), never an absolute
// URL or a protocol-relative one (e.g. "//evil.example.com") that could be used for an open redirect.
function isSafeReturnPath(path: string): boolean {
  return path.startsWith('/') && !path.startsWith('//')
}

/**
 * Builds the URL the Authentication API should redirect back to once sign-in completes.
 * Preserves the originally requested path (and its query string) so a user who signed in
 * from a deep link lands back there instead of always being sent to the home page.
 */
function buildAuthRedirectUrl(returnPath: string | null): string {
  const targetPath = returnPath && isSafeReturnPath(returnPath) ? returnPath : ROUTES.login
  const [path, search = ''] = targetPath.split('?')
  const redirectParameters = new URLSearchParams(search)
  redirectParameters.set(AUTH_REDIRECT_PARAM, 'true')

  return `${window.location.origin}${path}?${redirectParameters.toString()}`
}

export function getSignInUrl({
  identityProvider = DEFAULT_IDENTITY_PROVIDER_ID,
  returnPath = null,
}: { identityProvider?: string; returnPath?: string | null } = {}): string {
  const parameters = new URLSearchParams({
    idp_name: identityProvider,
    redirect_url: buildAuthRedirectUrl(returnPath),
  })

  return `${authenticationApiBaseUrl}/login?${parameters.toString()}`
}

export function getSignOutUrl(identityProvider: string = DEFAULT_IDENTITY_PROVIDER_ID): string {
  const parameters = new URLSearchParams({ idp_name: identityProvider, redirect_url: window.location.origin })

  return `${authenticationApiBaseUrl}/logout?${parameters.toString()}`
}

export async function fetchCurrentUser(): Promise<CurrentUser> {
  let response: Response

  try {
    response = await fetch(`${authenticationApiBaseUrl}/me`, { credentials: 'include' })
  } catch {
    // ponytail: treat an unreachable auth service (network/CORS failure, e.g. no backend running yet)
    // the same as "not logged in" so the user still lands on the login screen instead of a hard error.
    // Upgrade path: once the auth service is reliably deployed, surface connectivity issues distinctly.
    throw new UnauthenticatedError()
  }

  if (response.status === 401) {
    throw new UnauthenticatedError()
  }

  if (!response.ok) {
    throw new Error('Could not fetch current user')
  }

  const body = (await response.json()) as CurrentUserResponse
  const user = body.user
  const fallbackProfile = profileFromIdentity(user.id)
  const fullName = user.fullName?.trim()
  const initials = user.initials?.trim()
  const email = user.email?.trim() || (user.id.includes('@') ? user.id : undefined)

  return {
    ...user,
    fullName: fullName && fullName !== user.id ? fullName : fallbackProfile.fullName,
    initials: initials && initials !== '?' ? initials : fallbackProfile.initials,
    email,
  }
}
